import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "./SnapWidgetAccountDeletion.css";

const SUBMIT_URL = "https://blackdoor-captcha-backend-1.onrender.com/api/submit";
const RECAPTCHA_SITE_KEY = "6LdhLUsqAAAAANrQStf74sQIkq6lL_WaE2TSMuiV";

export default function SnapWidgetAccountDeletion() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState(null);
  const captchaRef = useRef(null);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateForm = () => {
    const nextErrors = {};

    if (!email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!validateEmail(email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!reason.trim()) {
      nextErrors.reason = "Reason is required";
    } else if (reason.trim().split(/\s+/).length > 150) {
      nextErrors.reason = "Reason should not exceed 150 words";
    }

    if (!captchaToken) {
      nextErrors.captcha = "Please complete the captcha";
      captchaRef.current?.classList.add("account-delete-shake");
      setTimeout(() => {
        captchaRef.current?.classList.remove("account-delete-shake");
      }, 500);
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitState(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: "SnapWidget account deletion request",
          email: email.trim(),
          description: `SnapWidget account deletion request\n\nAssociated email: ${email.trim()}\nReason: ${reason.trim()}`,
          captchaToken,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSubmitState("success");
        setEmail("");
        setReason("");
        setErrors({});
        setCaptchaToken(null);
      } else {
        setSubmitState("error");
      }
    } catch (error) {
      console.error("Account deletion request failed:", error);
      setSubmitState("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="account-delete-page">
      <section className="account-delete-shell">
        <a className="account-delete-brand" href="/">
          BlackDoor
        </a>

        <div className="account-delete-grid">
          <div className="account-delete-copy">
            <p className="account-delete-kicker">SnapWidget</p>
            <h1>Request account deletion</h1>
            <p>
              Submit the email associated with your SnapWidget account and a short reason. We will review the request and contact you if we need more information.
            </p>
            <p>
              Account deletion requests are handled by BlackDoor support at{" "}
              <a href="mailto:support@blackdoorapps.com">support@blackdoorapps.com</a>.
            </p>
          </div>

          <form className="account-delete-form" onSubmit={handleSubmit}>
            <label>
              <span>Associated email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              {errors.email && <small>{errors.email}</small>}
            </label>

            <label>
              <span>Reason</span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Tell us why you want to delete the account"
              />
              {errors.reason && <small>{errors.reason}</small>}
            </label>

            <div ref={captchaRef} className="account-delete-captcha">
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={setCaptchaToken}
                onExpired={() => setCaptchaToken(null)}
              />
              {errors.captcha && <small>{errors.captcha}</small>}
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit request"}
            </button>

            {submitState === "success" && (
              <p className="account-delete-status success">
                Request received. We will review it and follow up if needed.
              </p>
            )}
            {submitState === "error" && (
              <p className="account-delete-status error">
                Sorry, we could not submit the request. Please try again.
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}
